import { useEffect, useRef, useState } from "react";
import type { TouchEvent as ReactTouchEvent } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/lib/types";
import { typeLabel } from "@/lib/catalog";

interface HeroSliderProps {
  items: MediaItem[];
}

export default function HeroSlider({ items }: HeroSliderProps) {
  const [index, setIndex] = useState(0);
  const touchStart = useRef<number | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [items.length]);

  const go = (next: number) => setIndex((next + items.length) % items.length);

  const onTouchStart = (e: ReactTouchEvent) => {
    touchStart.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: ReactTouchEvent) => {
    if (touchStart.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(delta) > 50) {
      go(delta < 0 ? index + 1 : index - 1);
    }
    touchStart.current = null;
  };

  const active = items[index];

  return (
    <section
      className="relative h-[440px] w-full overflow-hidden bg-background-950 md:h-[580px]"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title} - 金桔影院推荐`}
            className="h-full w-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 via-background-950/55 to-background-950/25"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-background-950/75 via-background-950/25 to-transparent"></div>

      <div className="relative mx-auto flex h-full w-full max-w-8xl flex-col justify-center px-4 md:px-6">
        <div key={active.id} className="max-w-2xl animate-fade-up">
          <div className="mb-4 flex flex-wrap items-center gap-2.5">
            <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50">
              今日焦点
            </span>
            <span className="rounded-full border border-background-100/40 px-3 py-1 text-xs font-medium text-background-100">
              {typeLabel(active.type)}
            </span>
            <span className="flex items-center gap-1 text-sm font-semibold text-primary-300">
              <i className="ri-star-fill text-primary-400"></i>
              {active.rating.toFixed(1)}
            </span>
          </div>
          <h1 className="font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
            {active.title}
          </h1>
          <p className="mt-4 hidden max-w-xl text-sm leading-relaxed text-background-100 sm:block md:text-base">
            {active.synopsis}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看详情
            </Link>
            <Link
              to={`/category/${active.type}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-100/50 bg-background-50/10 px-5 py-2.5 text-sm font-semibold text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20"
            >
              <i className="ri-compass-3-line"></i>
              浏览同类
            </Link>
          </div>
        </div>

        <div className="absolute bottom-6 left-4 right-4 flex items-center justify-between md:bottom-8 md:left-6 md:right-6">
          <div className="flex items-center gap-2">
            {items.map((item, i) => (
              <button
                key={item.id}
                type="button"
                aria-label={`切换到 ${item.title}`}
                onClick={() => setIndex(i)}
                className={`cursor-pointer rounded-full transition-all duration-300 ${
                  i === index ? "h-2 w-8 bg-primary-500" : "h-2 w-2 bg-background-100/50"
                }`}
              />
            ))}
          </div>
          <div className="hidden items-center gap-2 md:flex">
            <button
              type="button"
              aria-label="上一个"
              onClick={() => go(index - 1)}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-100/40 text-background-50 transition-colors hover:bg-background-50/15"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              type="button"
              aria-label="下一个"
              onClick={() => go(index + 1)}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-100/40 text-background-50 transition-colors hover:bg-background-50/15"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}